import { Box, Flex, Grid, GridItem, Heading, Skeleton } from '@chakra-ui/react';
import {
  Icons8IconsApiResponse,
  Icons8IllustrationResponse,
  Icons8Params,
  fetchE2Icons8Api,
  listIcons8Illustrations,
} from 'libs/e2api/icons8';
import { useAppSelector } from 'modules/Editor/store';
import { selectRecommendedIconPack } from 'modules/Editor/store/editorSettingsSelector';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import useSWRImmutable from 'swr/immutable';
import { customStyles } from 'theme/custom';

import { RECOMMENDED_ICON_PACK_ID } from '../IconWidgetMenu.config';
import { Icon } from './Icon';
import { IconPackData } from './IconView.types';

const NUM_PREVIEW_ICONS = 3;
const DEFAULT_CATEGORY = 'free-icons';

/**
 * Fetches the preview ids for the Icons8 pack
 *
 * Atttempts to first filter by the default category to get more relevant results
 * If no results are found, fetches without category filter
 **/
const fetchIcons8PackPreviewIds = async (iconPackPlatform: string, token: string) => {
  const icons8IconsSearchParams: Icons8Params = {
    platform: iconPackPlatform,
    limit: NUM_PREVIEW_ICONS,
    offset: 0,
  };
  try {
    const icons8Resp = await fetchE2Icons8Api(
      {
        ...icons8IconsSearchParams,
        category: DEFAULT_CATEGORY,
      },
      token,
    ).then((content) => content);

    if (
      !icons8Resp ||
      icons8Resp.success !== true ||
      !icons8Resp.icons?.length ||
      icons8Resp.icons.length < NUM_PREVIEW_ICONS
    ) {
      return fetchE2Icons8Api(icons8IconsSearchParams, token).then((content) => content);
    }

    return icons8Resp;
  } catch (error) {
    // Error fetching with category or no icons found, try without category filter
    return fetchE2Icons8Api(icons8IconsSearchParams, token).then((content) => content);
  }
};

/**
 * Fetch icons8 illustration preview data
 *
 * @returns
 */
const fetchIllustrationPreviews = async (style: string, token: string) => {
  return listIcons8Illustrations(
    {
      categories: '',
      style,
      page: 1,
      perPage: NUM_PREVIEW_ICONS,
    },
    token,
  ).then((content) => content);
};

/**
 * Fetch the preview data for the recommended icon pack
 * TODO - support in-house algolia icons
 *
 * Returns array of preview data:
 * {
 *  id: string,
 *  thumbnailUrl: string  // URL to the thumbnail image for illustrations
 * }[]
 **/
const useIconsPackPreview = (filter: string, type?: 'icons8' | 'icons8Illustration') => {
  const token = useLoaderData() as string;

  const fetcher = type === 'icons8' ? fetchIcons8PackPreviewIds : fetchIllustrationPreviews;

  // Skip fetch if no platform specified
  const { data, error } = useSWRImmutable<object | null>(
    filter && type ? `ICONS-PACK-PREVIEW-${type}-${filter}` : null,
    () => fetcher(filter, token),
    { shouldRetryOnError: false },
  );

  const iconPreviews = useMemo(() => {
    if (type === 'icons8') {
      return (data as Icons8IconsApiResponse)?.icons.map((icon) => ({ id: icon.id, thumbnailUrl: '' })) || [];
    } else {
      return (
        (data as Icons8IllustrationResponse)?.illustrations.map((icon) => ({
          id: icon.id,
          thumbnailUrl: icon.urls.thumb.url,
        })) || []
      );
    }
  }, [data, type]);

  if (!data && !error) {
    return { iconPreviews: [] };
  } else if (!!error) {
    console.error('Error fetching Icons8 pack preview', error);
    return { iconPreviews: [] };
  }

  return {
    iconPreviews,
  };
};

interface RecommendedIconPackProps {
  onSelectIconPack: (iconPack: IconPackData) => void;
}

/**
 * RECOMMENDED ICON PACK COMPONENT
 *
 * Renders recommended icon pack preview in icon widget menu based on `recommendedIconPack` redux state
 * Handles fetching pack preview data and selection of the recommended icon pack.
 * Includes loading state while fetching data.
 *
 * @returns
 */
export const RecommendedIconPack = ({ onSelectIconPack }: RecommendedIconPackProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });

  const recommendedIconPack = useAppSelector(selectRecommendedIconPack);

  // Preview ids for icon - these are not known on first mount
  const { iconPreviews } = useIconsPackPreview(recommendedIconPack?.filter || '', recommendedIconPack?.type);

  const handleSelectIconPack = () => {
    if (!recommendedIconPack || !recommendedIconPack.filter) {
      return;
    }
    const isIcons8 = recommendedIconPack.type === 'icons8';
    const isIcons8Illustration = recommendedIconPack.type === 'icons8Illustration';

    onSelectIconPack({
      id: RECOMMENDED_ICON_PACK_ID,
      name: 'Recommended',
      isDiverse: false,
      isMono: recommendedIconPack.isMono,
      permission: 'default',
      order: 0,
      isIcons8,
      isIcons8Illustration,
      platform: isIcons8 ? recommendedIconPack.filter : undefined,
      style: isIcons8Illustration ? recommendedIconPack.filter : undefined,
    });
  };

  if (!recommendedIconPack) {
    return null;
  }

  // Icon preview ids not yet available, show loading state
  if (!iconPreviews || !iconPreviews.length) {
    return (
      <Box
        w='full'
        h='full'
        boxShadow='0px 0px 8px 0px #7047EB4D'
        borderRadius='lg'
        border='2px solid transparent'
        background={customStyles.container.violetGradient.background}
      >
        <Skeleton w='full' h='full' opacity={0.3} startColor='violetGradient.start' endColor='violetGradient.end' />
      </Box>
    );
  }

  return (
    <Box
      as='button'
      onClick={handleSelectIconPack}
      p={3}
      cursor='pointer'
      boxShadow='0px 0px 8px 0px #7047EB4D'
      borderRadius='lg'
      border='2px solid transparent'
      background={customStyles.container.violetGradient.background}
      transition='background 200ms ease-in-out'
      _hover={{
        bg: 'linear-gradient(var(--vg-colors-gray-50), var(--vg-colors-gray-50)) padding-box, linear-gradient(to bottom, var(--vg-colors-violetGradient-start), var(--vg-colors-violetGradient-end)) border-box',
      }}
      w='full'
    >
      <Flex justifyContent='space-between' alignItems='center'>
        <Heading fontWeight='semibold' as='h3' size='sm' color='font.500'>
          {t('recommendedPack')}
        </Heading>
      </Flex>
      <Grid templateColumns='repeat(3, 1fr)' gap={4}>
        {iconPreviews.map((preview, idx) => (
          <GridItem key={idx} p={2} h={100}>
            <Icon
              iconId={preview.id}
              isIcons8={recommendedIconPack?.type === 'icons8'}
              isIcons8Illustration={recommendedIconPack?.type === 'icons8Illustration'}
              thumbnailUrl={preview.thumbnailUrl}
            />
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};
